/**
 * The three message boxes she writes in most keep their words, on this
 * phone, for this sign-in.
 *
 * Bobbie, 2026-09-14: "I also sent a message earlier today and it did not
 * save or send to the person." The send-path map found that no composer in
 * the Hub kept its words anywhere but React state: a two-tap send left
 * half done, a Back mid-send, a killed app, a stale-build reload, or a
 * Messages row moving to Past all lost the text with no error. Then, on
 * 2026-09-15: "drafts would be nice as well."
 *
 * This is the store. Import-free on purpose so it runs under
 * --experimental-strip-types in tests; the React hook that drives it is
 * useComposerDraft.ts. Rules, each one paid for somewhere:
 *
 * - localStorage, keyed by SIGN-IN and BOX ("ldg:draft:{uid}:{scope}"), so
 *   one phone shared by two accounts never shows one person the other's
 *   words. A Jr Staff comes in two shapes (lib/jrStaffAliases.ts): promoted
 *   on her own dancer sign-in, where the Firebase principal is minted for
 *   the same uid as her dancer cookie, so one uid and one set of kept
 *   words; or a dancer sign-in AND a separate email account, two uids
 *   linked by users/{uid}.studentId, so two keys on one phone. Either way
 *   it is device state, not what she sees or receives.
 * - Every storage call is wrapped: a private window or blocked site data
 *   makes localStorage throw, and a draft is a convenience, never a crash.
 * - Text and recipient uids only. Never an attachment: bytes belong to the
 *   reservation gateway and a stored blob would outlive its reservation.
 * - A draft older than DRAFT_MAX_AGE_MS is dropped on read, so a phone does
 *   not surface a two-month-old sentence into a fresh conversation.
 * - Sign-out clears every draft for that uid (clearComposerDraftsFor), the
 *   same sweep the sign-out wrapper already makes for other per-account
 *   state. A draft must not outlive the account on the device.
 */

export interface ComposerDraft {
  text: string;
  /** New message page only: who it was going to. Empty elsewhere. */
  recipientUids: string[];
  /** Epoch millis of the last write, for the age check. */
  savedAt: number;
}

/** The subset of Storage this file touches, so tests can hand in a Map. */
export interface DraftStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  key(index: number): string | null;
  readonly length: number;
}

export const DRAFT_KEY_PREFIX = 'ldg:draft:';
export const DRAFT_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;
export const DRAFT_TEXT_MAX = 20_000;
export const DRAFT_RECIPIENTS_MAX = 200;

function defaultStorage(): DraftStorage | null {
  try {
    const candidate = (globalThis as { localStorage?: DraftStorage }).localStorage;
    return candidate ?? null;
  } catch {
    return null;
  }
}

/** One key per sign-in and box. Scopes: `new`, `thread:{threadId}`. */
export function composerDraftKey(uid: string, scope: string): string {
  return `${DRAFT_KEY_PREFIX}${uid}:${scope}`;
}

function cleanRecipientUids(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  for (const item of value) {
    if (typeof item === 'string' && item.trim() && seen.size < DRAFT_RECIPIENTS_MAX) seen.add(item.trim());
  }
  return [...seen];
}

/**
 * The draft for this sign-in and box, or null: none saved, unreadable,
 * malformed, empty, or older than the cutoff (which is also removed, so a
 * stale row does not sit there forever).
 */
export function readComposerDraft(
  uid: string,
  scope: string,
  storage: DraftStorage | null = defaultStorage(),
  now: number = Date.now()
): ComposerDraft | null {
  if (!uid || !scope || !storage) return null;
  const key = composerDraftKey(uid, scope);
  let raw: string | null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    parsed = null;
  }
  const record = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  const text = record && typeof record.text === 'string' ? record.text.slice(0, DRAFT_TEXT_MAX) : '';
  const savedAt = record && typeof record.savedAt === 'number' && Number.isFinite(record.savedAt) ? record.savedAt : 0;
  const recipientUids = cleanRecipientUids(record?.recipientUids);
  const stale = !savedAt || now - savedAt > DRAFT_MAX_AGE_MS;
  if (!record || stale || (!text.trim() && recipientUids.length === 0)) {
    try {
      storage.removeItem(key);
    } catch {
      // A store that cannot forget is still a store that could not hurt.
    }
    return null;
  }
  return { text, recipientUids, savedAt };
}

/**
 * Save the words (and, on the New message page, the picks). An empty
 * draft is a removal, so a box she cleared herself does not come back.
 */
export function writeComposerDraft(
  uid: string,
  scope: string,
  draft: { text: string; recipientUids?: string[] },
  storage: DraftStorage | null = defaultStorage(),
  now: number = Date.now()
): void {
  if (!uid || !scope || !storage) return;
  const text = typeof draft.text === 'string' ? draft.text.slice(0, DRAFT_TEXT_MAX) : '';
  const recipientUids = cleanRecipientUids(draft.recipientUids);
  if (!text.trim() && recipientUids.length === 0) {
    clearComposerDraft(uid, scope, storage);
    return;
  }
  const value: ComposerDraft = { text, recipientUids, savedAt: now };
  try {
    storage.setItem(composerDraftKey(uid, scope), JSON.stringify(value));
  } catch {
    // Quota or a private window: the words stay in the box, just not here.
  }
}

export function clearComposerDraft(
  uid: string,
  scope: string,
  storage: DraftStorage | null = defaultStorage()
): void {
  if (!uid || !scope || !storage) return;
  try {
    storage.removeItem(composerDraftKey(uid, scope));
  } catch {
    // See writeComposerDraft.
  }
}

/** Sign-out: every draft this sign-in left on this device goes. */
export function clearComposerDraftsFor(uid: string, storage: DraftStorage | null = defaultStorage()): number {
  if (!uid || !storage) return 0;
  const prefix = `${DRAFT_KEY_PREFIX}${uid}:`;
  const doomed: string[] = [];
  try {
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith(prefix)) doomed.push(key);
    }
    for (const key of doomed) storage.removeItem(key);
  } catch {
    return doomed.length;
  }
  return doomed.length;
}
